import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { createPoll, generatePollLocalizations, getPoll, testSendPoll, updatePoll } from '../Api';
import type { PollDto, PollTexts, PollUpsertRequest } from '../models/dtos/PollDto';
import { POLL_LANGUAGE_LABELS, POLL_LANGUAGES } from '../models/dtos/PollDto';
import { PollStatusChip } from '../components/polls/PollShared';

/** Mirrors the server limits in PollAdminService. */
const LIMITS = {
    question: 300,
    pushTitle: 80,
    pushBody: 200,
    customAnswerHint: 100,
    thankYouMessage: 300,
    option: 120,
    options: 10,
};

type TextKey = keyof PollTexts;
type TextsForm = Record<string, Record<TextKey, string>>;
interface OptionForm {
    localId: number;
    key?: string | null;
    texts: Record<string, string>;
}

const TEXT_FIELDS: { key: TextKey; label: string; multiline?: boolean; helper: string }[] = [
    { key: 'question', label: 'Question', multiline: true, helper: 'Required in the default language' },
    { key: 'pushTitle', label: 'Push title', helper: 'Empty = the question' },
    { key: 'pushBody', label: 'Push text', multiline: true, helper: 'Empty = the question' },
    { key: 'customAnswerHint', label: 'Custom answer placeholder', helper: 'Empty = app default (“Write your answer”)' },
    { key: 'thankYouMessage', label: 'Thank-you message', multiline: true, helper: 'Empty = app default' },
];

const emptyTexts = (): Record<TextKey, string> => ({
    question: '', pushTitle: '', pushBody: '', customAnswerHint: '', thankYouMessage: '',
});

let nextLocalId = 1;
const newOption = (texts: Record<string, string> = {}, key?: string | null): OptionForm => ({ localId: nextLocalId++, key, texts });

const toForm = (poll: PollDto): { texts: TextsForm; options: OptionForm[] } => {
    const texts: TextsForm = {};
    Object.entries(poll.texts).forEach(([lang, value]) => {
        texts[lang] = {
            question: value.question ?? '',
            pushTitle: value.pushTitle ?? '',
            pushBody: value.pushBody ?? '',
            customAnswerHint: value.customAnswerHint ?? '',
            thankYouMessage: value.thankYouMessage ?? '',
        };
    });
    return { texts, options: poll.options.map(o => newOption({ ...o.texts }, o.key)) };
};

const filledLanguage = (texts: TextsForm, options: OptionForm[], lang: string) =>
    Object.values(texts[lang] ?? {}).some(v => v.trim()) || options.some(o => (o.texts[lang] ?? '').trim());

export default function PollEditPage() {
    const id = useParams()['id'];
    const isEdit = !!id;
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(isEdit);
    const [saving, setSaving] = React.useState(false);
    const [poll, setPoll] = React.useState<PollDto | null>(null);
    const [name, setName] = React.useState('');
    const [defaultLanguage, setDefaultLanguage] = React.useState('en');
    const [allowCustomAnswer, setAllowCustomAnswer] = React.useState(true);
    const [customAnswerMaxLength, setCustomAnswerMaxLength] = React.useState('500');
    const [texts, setTexts] = React.useState<TextsForm>({ en: emptyTexts() });
    const [options, setOptions] = React.useState<OptionForm[]>([newOption(), newOption()]);
    const [lang, setLang] = React.useState('en');
    const [toast, setToast] = React.useState<{ severity: 'success' | 'error'; text: string } | null>(null);

    const [aiLanguages, setAiLanguages] = React.useState<string[]>(POLL_LANGUAGES.filter(l => l !== 'en'));
    const [aiImproveEnglish, setAiImproveEnglish] = React.useState(false);
    const [aiBusy, setAiBusy] = React.useState(false);

    const [testIds, setTestIds] = React.useState('');
    const [testBusy, setTestBusy] = React.useState(false);

    React.useEffect(() => {
        if (!id) return;
        (async () => {
            setLoading(true);
            const loaded = await getPoll(id);
            if (loaded) {
                const form = toForm(loaded);
                setPoll(loaded);
                setName(loaded.name);
                setDefaultLanguage(loaded.defaultLanguage);
                setLang(loaded.defaultLanguage);
                setAllowCustomAnswer(loaded.allowCustomAnswer);
                setCustomAnswerMaxLength(String(loaded.customAnswerMaxLength));
                setTexts(form.texts);
                setOptions(form.options);
            }
            setLoading(false);
        })();
    }, [id]);

    const text = (l: string, key: TextKey) => texts[l]?.[key] ?? '';
    const setText = (l: string, key: TextKey, value: string) =>
        setTexts(prev => ({ ...prev, [l]: { ...(prev[l] ?? emptyTexts()), [key]: value } }));
    const setOptionText = (localId: number, l: string, value: string) =>
        setOptions(prev => prev.map(o => (o.localId === localId ? { ...o, texts: { ...o.texts, [l]: value } } : o)));
    const moveOption = (index: number, delta: number) =>
        setOptions(prev => {
            const next = [...prev];
            const [item] = next.splice(index, 1);
            next.splice(index + delta, 0, item);
            return next;
        });

    const validationError = (): string | null => {
        if (!name.trim()) return 'Name is required.';
        if (!text(defaultLanguage, 'question').trim()) return `The question is required in ${defaultLanguage.toUpperCase()}.`;
        if (options.length === 0 && !allowCustomAnswer) return 'Add an option or allow custom answers.';
        if (options.some(o => !(o.texts[defaultLanguage] ?? '').trim())) {
            return `Every option needs a text in ${defaultLanguage.toUpperCase()}.`;
        }
        const max = Number(customAnswerMaxLength);
        if (allowCustomAnswer && (!Number.isInteger(max) || max < 1 || max > 1000)) return 'Custom answer length must be 1–1000.';
        return null;
    };

    const buildRequest = (): PollUpsertRequest => {
        const payloadTexts: Record<string, PollTexts> = {};
        Object.entries(texts).forEach(([l, value]) => {
            const cleaned: PollTexts = {};
            (Object.keys(value) as TextKey[]).forEach(k => {
                const v = value[k].trim();
                if (v) cleaned[k] = v;
            });
            if (Object.keys(cleaned).length > 0) payloadTexts[l] = cleaned;
        });
        return {
            name: name.trim(),
            defaultLanguage,
            texts: payloadTexts,
            options: options.map(o => ({
                key: o.key ?? null,
                texts: Object.fromEntries(Object.entries(o.texts).map(([l, v]) => [l, v.trim()]).filter(([, v]) => v)),
            })),
            allowCustomAnswer,
            customAnswerMaxLength: Number(customAnswerMaxLength) || 500,
        };
    };

    const save = async () => {
        const error = validationError();
        if (error) {
            setToast({ severity: 'error', text: error });
            return;
        }
        setSaving(true);
        const result = isEdit ? await updatePoll(id as string, buildRequest()) : await createPoll(buildRequest());
        setSaving(false);
        if (!result.ok) {
            setToast({ severity: 'error', text: result.error });
            return;
        }
        setToast({ severity: 'success', text: 'Poll saved.' });
        if (!isEdit) {
            navigate('/marketing/polls/' + encodeURIComponent(result.data.id) + '/edit', { replace: true });
            return;
        }
        const form = toForm(result.data);
        setPoll(result.data);
        setOptions(form.options);
    };

    const generate = async () => {
        if (!text('en', 'question').trim()) {
            setToast({ severity: 'error', text: 'Write the English texts first — translations are made from them.' });
            return;
        }
        setAiBusy(true);
        const result = await generatePollLocalizations({
            texts: Object.fromEntries(Object.entries(texts.en ?? emptyTexts()).map(([k, v]) => [k, v.trim() || null])),
            options: options.map((o, index) => ({ key: o.key ?? `new-${index}`, texts: { en: o.texts.en ?? '' } })),
            supportedLanguages: aiLanguages,
            improveEnglish: aiImproveEnglish,
        });
        setAiBusy(false);
        if (!result.ok) {
            setToast({ severity: 'error', text: result.error });
            return;
        }
        setTexts(prev => {
            const next = { ...prev };
            Object.entries(result.data.texts).forEach(([l, value]) => {
                next[l] = {
                    question: value.question ?? '',
                    pushTitle: value.pushTitle ?? '',
                    pushBody: value.pushBody ?? '',
                    customAnswerHint: value.customAnswerHint ?? '',
                    thankYouMessage: value.thankYouMessage ?? '',
                };
            });
            return next;
        });
        setOptions(prev => prev.map((o, index) => {
            const generated = result.data.options[index];
            return generated ? { ...o, texts: { ...o.texts, ...generated.texts } } : o;
        }));
        setToast({ severity: 'success', text: `Translated into ${Object.keys(result.data.texts).length - 1} languages. Review, then save.` });
    };

    const sendTest = async () => {
        const ids = testIds.split(/[\s,;]+/).map(v => Number(v)).filter(v => Number.isInteger(v) && v > 0);
        if (ids.length === 0 || !id) {
            setToast({ severity: 'error', text: 'Enter BETUP user ids.' });
            return;
        }
        setTestBusy(true);
        const result = await testSendPoll(id, ids);
        setTestBusy(false);
        if (!result.ok) {
            setToast({ severity: 'error', text: result.error });
            return;
        }
        const send = result.data;
        setToast({
            severity: send.pushedCount > 0 ? 'success' : 'error',
            text: `Test push: ${send.pushedCount} queued, ${send.skippedAnsweredCount} skipped (answered for real), ${send.failedCount} not queued.`,
        });
    };

    if (loading) {
        return <Box sx={{ mx: 3, my: 3 }}><CircularProgress /></Box>;
    }

    const previewTexts = (key: TextKey) => text(lang, key).trim() || text(defaultLanguage, key).trim();
    const previewOption = (o: OptionForm) => (o.texts[lang] ?? '').trim() || (o.texts[defaultLanguage] ?? '').trim();

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
                    <Stack direction="row" gap={1} alignItems="center">
                        <Typography variant="h4">{isEdit ? 'Edit poll' : 'New poll'}</Typography>
                        {poll && <PollStatusChip status={poll.status} />}
                    </Stack>
                    <Stack direction="row" gap={1}>
                        <Button onClick={() => navigate('/marketing/polls')}>Back</Button>
                        {isEdit && (
                            <Button variant="outlined" onClick={() => navigate('/marketing/polls/' + encodeURIComponent(id as string) + '/stats')}>
                                Stats
                            </Button>
                        )}
                        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                    </Stack>
                </Stack>

                {poll && poll.status !== 'DRAFT' && (
                    <Alert severity="info">
                        This poll was already sent. Text edits reach players who open it from now on; options somebody picked can't be removed.
                    </Alert>
                )}

                <Card variant="outlined">
                    <CardContent>
                        <Stack gap={2}>
                            <TextField label="Internal name" value={name} onChange={e => setName(e.target.value)} required
                                helperText="Dashboard only — players never see it" />
                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }}>
                                <TextField select label="Default language" value={defaultLanguage} sx={{ minWidth: 220 }}
                                    onChange={e => setDefaultLanguage(e.target.value)}
                                    helperText="Players whose language has no question get this one">
                                    {POLL_LANGUAGES.map(l => <MenuItem key={l} value={l}>{l.toUpperCase()} · {POLL_LANGUAGE_LABELS[l]}</MenuItem>)}
                                </TextField>
                                <FormControlLabel
                                    control={<Switch checked={allowCustomAnswer} onChange={e => setAllowCustomAnswer(e.target.checked)} />}
                                    label="Allow a custom answer"
                                />
                                {allowCustomAnswer && (
                                    <TextField label="Custom answer max length" value={customAnswerMaxLength} sx={{ width: 220 }}
                                        onChange={e => setCustomAnswerMaxLength(e.target.value)} />
                                )}
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="h6">Options</Typography>
                            <Button size="small" disabled={options.length >= LIMITS.options} onClick={() => setOptions(prev => [...prev, newOption()])}>
                                Add option
                            </Button>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Players pick one option{allowCustomAnswer ? ' or write their own answer' : ''}. Texts per language are edited below.
                        </Typography>
                        <Stack gap={1}>
                            {options.map((option, index) => (
                                <Stack key={option.localId} direction="row" gap={1} alignItems="center">
                                    <Chip size="small" label={option.key ?? 'new'} variant="outlined" sx={{ minWidth: 56 }} />
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label={`Option ${index + 1} (${defaultLanguage})`}
                                        value={option.texts[defaultLanguage] ?? ''}
                                        onChange={e => setOptionText(option.localId, defaultLanguage, e.target.value)}
                                        inputProps={{ maxLength: LIMITS.option }}
                                    />
                                    <IconButton size="small" disabled={index === 0} onClick={() => moveOption(index, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" disabled={index === options.length - 1} onClick={() => moveOption(index, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => setOptions(prev => prev.filter(o => o.localId !== option.localId))}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>

                <Stack direction={{ xs: 'column', lg: 'row' }} gap={3} alignItems="flex-start">
                    <Card variant="outlined" sx={{ flex: 1, width: '100%' }}>
                        <Tabs value={lang} onChange={(_, value) => setLang(value)} variant="scrollable" scrollButtons="auto"
                            sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            {POLL_LANGUAGES.map(l => (
                                <Tab key={l} value={l} title={POLL_LANGUAGE_LABELS[l]} sx={{ minWidth: 56, px: 1.5,
                                    color: filledLanguage(texts, options, l) ? 'text.primary' : 'text.disabled' }}
                                    label={`${l.toUpperCase()}${l === defaultLanguage ? ' *' : filledLanguage(texts, options, l) ? ' ✓' : ''}`} />
                            ))}
                        </Tabs>
                        <CardContent>
                            <Stack gap={2}>
                                <Typography variant="caption" color="text.secondary">
                                    {POLL_LANGUAGE_LABELS[lang]}{lang === defaultLanguage
                                        ? ' — default language: the question and every option are required.'
                                        : ' — optional. Players in this language get it only when the question is filled; empty fields fall back to the default language.'}
                                </Typography>
                                {TEXT_FIELDS.filter(f => allowCustomAnswer || f.key !== 'customAnswerHint').map(field => (
                                    <TextField
                                        key={field.key}
                                        label={`${field.label} (${lang})`}
                                        required={field.key === 'question' && lang === defaultLanguage}
                                        multiline={field.multiline}
                                        minRows={field.multiline ? 2 : undefined}
                                        value={text(lang, field.key)}
                                        onChange={e => setText(lang, field.key, e.target.value)}
                                        inputProps={{ maxLength: LIMITS[field.key], dir: lang === 'ar' ? 'rtl' : undefined }}
                                        helperText={`${field.helper} · ${text(lang, field.key).length}/${LIMITS[field.key]}`}
                                    />
                                ))}
                                {options.length > 0 && <Divider>Options ({lang})</Divider>}
                                {options.map((option, index) => (
                                    <TextField
                                        key={option.localId}
                                        size="small"
                                        label={`Option ${index + 1} (${lang})`}
                                        required={lang === defaultLanguage}
                                        value={option.texts[lang] ?? ''}
                                        placeholder={option.texts[defaultLanguage] ?? ''}
                                        onChange={e => setOptionText(option.localId, lang, e.target.value)}
                                        inputProps={{ maxLength: LIMITS.option, dir: lang === 'ar' ? 'rtl' : undefined }}
                                    />
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>

                    <Stack gap={3} sx={{ width: { xs: '100%', lg: 360 } }}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="overline">Preview · {lang.toUpperCase()}</Typography>
                            <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', borderRadius: 2, p: 1.5, mb: 2 }}>
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>BETUP · now</Typography>
                                <Typography variant="subtitle2">{previewTexts('pushTitle') || previewTexts('question') || 'Push title'}</Typography>
                                <Typography variant="body2">{previewTexts('pushBody') || previewTexts('question') || 'Push text'}</Typography>
                            </Box>
                            <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', borderRadius: 3, p: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>{previewTexts('question') || 'Question'}</Typography>
                                <Stack gap={1}>
                                    {options.map(o => (
                                        <Box key={o.localId} sx={{ border: 1, borderColor: 'grey.700', borderRadius: 2, px: 1.5, py: 1 }}>
                                            <Typography variant="body2">{previewOption(o) || '…'}</Typography>
                                        </Box>
                                    ))}
                                    {allowCustomAnswer && (
                                        <Box sx={{ border: 1, borderStyle: 'dashed', borderColor: 'grey.600', borderRadius: 2, px: 1.5, py: 1 }}>
                                            <Typography variant="body2" sx={{ opacity: 0.6 }}>{previewTexts('customAnswerHint') || 'Write your answer'}</Typography>
                                        </Box>
                                    )}
                                </Stack>
                                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.7 }}>
                                    After answering: {previewTexts('thankYouMessage') || 'Thanks for your answer!'}
                                </Typography>
                            </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Translate with AI</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                Translates the English texts and options into the picked languages (overwrites them). Review before saving.
                            </Typography>
                            <Autocomplete
                                multiple
                                size="small"
                                options={POLL_LANGUAGES.filter(l => l !== 'en') as string[]}
                                value={aiLanguages}
                                onChange={(_, value) => setAiLanguages(value)}
                                getOptionLabel={l => `${l.toUpperCase()} · ${POLL_LANGUAGE_LABELS[l] ?? l}`}
                                renderInput={params => <TextField {...params} label="Languages" />}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={aiImproveEnglish} onChange={e => setAiImproveEnglish(e.target.checked)} />}
                                label="Polish the English first"
                            />
                            <Button variant="outlined" fullWidth onClick={generate} disabled={aiBusy || aiLanguages.length === 0}>
                                {aiBusy ? 'Translating…' : 'Generate translations'}
                            </Button>
                        </Paper>

                        {isEdit && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Test send</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Push the saved poll to specific accounts. Test answers are kept out of the stats and reset on every test send.
                                </Typography>
                                <Stack direction="row" gap={1}>
                                    <TextField size="small" fullWidth label="BETUP user ids" placeholder="123, 456" value={testIds}
                                        onChange={e => setTestIds(e.target.value)} />
                                    <Tooltip title={poll?.status === 'CLOSED' ? 'Reopen the poll to send' : ''}>
                                        <span>
                                            <Button variant="outlined" onClick={sendTest} disabled={testBusy || poll?.status === 'CLOSED'}>
                                                {testBusy ? '…' : 'Send'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </Stack>
            </Stack>
            {toast && (
                <Snackbar open autoHideDuration={5000} onClose={() => setToast(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.text}</Alert>
                </Snackbar>
            )}
        </Box>
    );
}
